/**
 * DrillCardEnhanced Component
 * Enhanced drill card with status badges and drill images
 * FT-16: Mobile responsive pass - larger text, 44px+ touch targets, optimized info density
 */

import React from 'react';
import {
  Calendar, AlertCircle, AlertTriangle, CheckCircle, Clock,
  Shield, LifeBuoy, Flame, Ship, Heart,
  Users, Navigation, Droplet, ShieldAlert, Lock, Power
} from 'lucide-react';

const DrillCardEnhanced = ({ drill, onRecordDrill, onViewDrill, onViewHistory }) => {
  // Get image for drill type
  const getDrillImage = () => {
    const type = (drill.drill_type || '').toLowerCase();
    
    // Specific drill images
    if (type.includes('fire')) {
      return '/images/drills/fire-drill.jpg';
    }
    if (type.includes('abandon') || type.includes('ship')) {
      return '/images/drills/abandon-ship.jpg';
    }
    if (type.includes('man_overboard') || type.includes('mob') || 
        (type.includes('man') && type.includes('overboard'))) {
      return '/images/drills/man-overboard.jpg';
    }
    
    // Default drill category image
    return '/images/drills/drill-category.jpg';
  };

  // Get icon for drill type (fallback if image fails)
  const getDrillIcon = () => {
    const type = (drill.drill_type || '').toLowerCase();
    
    if (type.includes('fire')) return Flame;
    if (type.includes('abandon')) return Ship;
    if (type.includes('man') && type.includes('overboard')) return LifeBuoy;
    if (type.includes('medical')) return Heart;
    if (type.includes('security')) return Lock;
    if (type.includes('pollution') || type.includes('spill')) return Droplet;
    if (type.includes('emergency') && type.includes('steering')) return Navigation;
    if (type.includes('collision')) return ShieldAlert;
    if (type.includes('power') || type.includes('blackout')) return Power;
    if (type.includes('muster')) return Users;
    
    return ShieldAlert; // Default icon
  };

  // Get status display
  const getStatusDisplay = () => {
    switch (drill.status) {
      case 'overdue':
        return {
          icon: AlertCircle,
          color: 'text-red-600',
          bgColor: 'bg-red-100',
          borderColor: 'border-red-200',
          label: drill.daysUntilDue != null && !isNaN(drill.daysUntilDue) 
            ? `${Math.abs(drill.daysUntilDue)} days overdue`
            : 'Overdue',
          priority: 'high'
        };
      case 'due_soon':
        return {
          icon: AlertTriangle,
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-100',
          borderColor: 'border-yellow-200',
          label: drill.daysUntilDue != null && !isNaN(drill.daysUntilDue) 
            ? `Due in ${drill.daysUntilDue} days`
            : 'Due soon',
          priority: 'medium'
        };
      case 'compliant':
        const days = drill.daysUntilDue;
        let label = 'Compliant';
        
        if (days != null && !isNaN(days)) {
          if (days === 0) {
            label = 'Due today';
          } else if (days === 1) {
            label = 'Due tomorrow';
          } else if (days <= 30) {
            label = `Due in ${days} days`;
          } else if (days <= 60) {
            label = 'Due in 2 months';
          } else if (days <= 90) {
            label = 'Due in 3 months';
          } else {
            label = `Due in ${Math.floor(days / 30)} months`;
          }
        }
        
        return {
          icon: CheckCircle,
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          borderColor: 'border-green-200',
          label,
          priority: 'low'
        };
      default:
        return {
          icon: Clock,
          color: 'text-gray-500',
          bgColor: 'bg-gray-100',
          borderColor: 'border-gray-200',
          label: 'No due date',
          priority: 'none'
        };
    }
  };

  const statusDisplay = getStatusDisplay();
  const StatusIcon = statusDisplay.icon;
  const DrillIcon = getDrillIcon();

  // Format drill name for display
  const formatDrillName = (drillType) => {
    if (!drillType) return 'Unknown Drill';
    return drillType
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get frequency display - handles both old and new format
  const getFrequencyDisplay = () => {
    const freq = drill.frequencyDays || drill.frequency_days;
    if (!freq) return 'As required';
    if (freq === 30) return 'Monthly';
    if (freq === 60) return 'Bi-monthly';
    if (freq === 90) return 'Quarterly';
    if (freq === 180) return '6 Monthly';
    if (freq === 365) return 'Annual';
    return `Every ${freq} days`;
  };

  // Get compliance level text - used in subtitle
  const getComplianceLevelText = () => {
    const level = drill.complianceLevel || drill.compliance_level;
    if (level) return level;
    return 'Scheduled drill';
  };

  return (
    <div
      className="bg-white rounded-xl shadow-sm hover:shadow-md active:bg-gray-50 transition-all duration-200 overflow-hidden border border-gray-200 cursor-pointer min-h-[72px]"
      onClick={() => onRecordDrill(drill.drill_type)}
    >
      {/* FT-16: Mobile-optimized padding */}
      <div className="p-4 md:p-4">
        <div className="flex items-start gap-2 sm:gap-3">
          {/* Drill Image */}
          <div className="flex-shrink-0">
            <img
              src={getDrillImage()}
              alt={formatDrillName(drill.drill_type)}
              className="w-14 h-14 md:w-16 md:h-16 rounded-lg object-cover shadow-sm"
              onError={(e) => {
                // Fallback to icon if image fails
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div
              className="w-14 h-14 md:w-16 md:h-16 rounded-lg bg-gray-100 flex items-center justify-center"
              style={{ display: 'none' }}
            >
              <DrillIcon className="h-7 w-7 md:h-8 md:h-8 text-gray-400" />
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-1">
              <div className="flex-1 min-w-0">
                {/* FT-16: Larger drill name on mobile */}
                <div className="flex items-center gap-1">
                  <h3 className="text-base md:text-sm font-semibold text-gray-900 truncate">
                    {formatDrillName(drill.drill_type)}
                  </h3>
                </div>
                <p className="text-sm md:text-xs text-gray-500 mt-0.5 truncate">
                  {getComplianceLevelText()}
                </p>
              </div>
              
              {/* FT-16: Status Badge - larger on mobile for readability */}
              <span className={`inline-flex items-center px-2 md:px-2 py-1 md:py-0.5 rounded-full text-sm md:text-xs font-medium ${statusDisplay.bgColor} ${statusDisplay.color}`}>
                <StatusIcon className="h-3.5 md:h-3 w-3.5 md:w-3 mr-1" />
                <span>{statusDisplay.label}</span>
              </span>
            </div>

            {/* FT-16: Meta Information - larger text on mobile */}
            <div className="flex flex-wrap items-center gap-3 md:gap-3 mt-2 md:mt-2">
              <span className="inline-flex items-center text-sm md:text-xs text-gray-500">
                <Clock className="h-3.5 md:h-3 w-3.5 md:w-3 mr-1" />
                {getFrequencyDisplay()}
              </span>

              <span className="inline-flex items-center text-sm md:text-xs text-gray-500">
                <Calendar className="h-3.5 md:h-3 w-3.5 md:w-3 mr-1" />
                <span className="hidden md:inline">Last: {drill.lastDrill ? formatDate(drill.lastDrill.date) : 'Never'}</span>
                <span className="md:hidden">{drill.lastDrill ? formatDate(drill.lastDrill.date) : 'Never'}</span>
              </span>

              {drill.equipmentDescription && (
                <span className="hidden md:inline-flex items-center text-xs text-gray-500">
                  <Shield className="h-3 w-3 mr-1" />
                  {drill.equipmentDescription}
                </span>
              )}
            </div>
            
            {/* Category label - visual anchor */}
            <div className="flex items-center justify-between mt-2 sm:mt-3">
              <span className="text-xs sm:text-xs text-gray-400 uppercase tracking-wider">
                Safety Drill
              </span>
              {drill.regulatorySource || drill.regulatory_source ? (
                <span className="hidden sm:inline-flex items-center text-xs text-gray-500">
                  <Shield className="h-3 w-3 mr-1" />
                  {drill.regulatorySource || drill.regulatory_source}
                </span>
              ) : null}
            </div>
          </div>
        </div>
        
        {/* Additional Information */}
        {drill.lastDrill && drill.lastDrill.success === false && (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <p className="text-xs text-orange-600 flex items-center">
              <AlertTriangle className="h-3 w-3 mr-1 flex-shrink-0" />
              Last drill unsuccessful - follow-up required
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DrillCardEnhanced;